import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { TEMPLATE_LABELS, type PreviewDevice } from '@game-platform/theme-constructor';
import { isPreviewTemplate, type PreviewTemplate } from '@game-platform/pack-animation';
import { useDefaultTemplate } from '../Themes/useDefaultTemplate';
import { AnimationEditorPanel } from './AnimationEditorPanel';
import { AnimationPreview, type AnimationPreviewHandle } from './AnimationPreview';
import { AnimationTopBar } from './AnimationTopBar';
import { findPack } from './animationScope';
import { PreviewActions } from './PreviewActions';
import { buildPreviewPayload, IFRAME_ORIGIN } from './previewPayload';
import { ScopeBanner } from './ScopeBanner';
import { useAnimationEditor } from './useAnimationEditor';
import { usePreviewPacks } from './usePreviewPacks';
import { useScopedAnimation } from './useScopedAnimation';

export function AnimationPage() {
  const { template } = useParams<{ template: string }>();
  const navigate = useNavigate();
  const defaultTemplate = useDefaultTemplate();
  // URL is the single source of truth for the template (see ThemeEditorPage for why);
  // the bare /animation route lands on the default template before we get here.
  const activeTemplate: PreviewTemplate = isPreviewTemplate(template) ? template : 'classic';
  const setActiveTemplate = (next: PreviewTemplate) => {
    if (next !== activeTemplate) navigate(`/animation/${next}`, { replace: true });
  };

  const [state, dispatch] = useAnimationEditor();
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  // Preview-only: which rarity the pretend pull has ('' = a different one each cycle)
  const [previewRarity, setPreviewRarity] = useState('');
  const previewRef = useRef<AnimationPreviewHandle>(null);
  const { packs, packId, setPackId, loadPacks, replacePack, pinPack } = usePreviewPacks(setPreviewError);
  // The global animation is per template: off the default one, say which template it lands in
  const globalAudience =
    activeTemplate === defaultTemplate ? 'all packs' : `all packs (${TEMPLATE_LABELS[activeTemplate]})`;
  const scoped = useScopedAnimation({
    template: activeTemplate,
    globalAudience,
    packs,
    packId,
    setPackId,
    replacePack,
    pinPack,
    editor: [state, dispatch],
  });
  const { designSystem } = scoped;

  const selectedPack = findPack(packs, packId);
  const packImageUrl = selectedPack?.pack_image_url ?? null;
  // Real cards of the pack; the preview draws a different one each cycle and
  // falls back to its own placeholder when the list is empty
  const cardImageUrls = selectedPack?.sample_card_image_urls;

  const payload = useMemo(
    () =>
      buildPreviewPayload({
        animation: state.animation,
        designSystem,
        template: activeTemplate,
        packImageUrl,
        cardImageUrls,
        rarity: previewRarity,
      }),
    [designSystem, packImageUrl, cardImageUrls, previewRarity, state.animation, activeTemplate],
  );
  const saveLabel = scoped.scope === 'pack' ? 'Save for this pack' : `Save for ${globalAudience}`;
  // A pack with its own opening plays it in every template; one without follows each template's global
  const templateHint =
    scoped.scope !== 'pack'
      ? undefined
      : scoped.editedPack?.animation
        ? "Preview look only: this pack's opening plays the same in every template"
        : 'This pack plays the global animation of each template until you save its own';

  const onPreviewError = useCallback((m: string) => setPreviewError(m), []);

  if (scoped.loadError) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-800">
          {scoped.loadError}
        </div>
      </div>
    );
  }
  if (!designSystem) {
    return (
      <div className="p-6 text-[14px] text-[var(--co-muted)]">Loading animation settings...</div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <AnimationTopBar
        template={activeTemplate}
        templateHint={templateHint}
        defaultTemplate={defaultTemplate}
        onTemplateChange={setActiveTemplate}
        packs={packs}
        packId={packId}
        onPackChange={scoped.changePack}
        scope={scoped.scope}
        onScopeChange={scoped.changeScope}
        packScopeBlocker={scoped.packScopeBlocker}
        locked={scoped.busy}
        rarity={previewRarity}
        onRarityChange={setPreviewRarity}
        device={device}
        onDeviceChange={setDevice}
        actions={
          <PreviewActions
            variant={state.animation.variant}
            hasOptions={Boolean(state.animation.options)}
            message={scoped.message}
            busy={scoped.busy}
            canSave={scoped.canSave}
            saveLabel={saveLabel}
            onCommand={(command) => previewRef.current?.send(command)}
            onResetPreview={() => {
              setPreviewError(null);
              previewRef.current?.send('reset');
              void loadPacks();
            }}
            onResetOptions={() => dispatch({ type: 'reset-options' })}
            onSave={() => void scoped.save()}
          />
        }
      />

      <div className="flex flex-1 overflow-hidden">
        <AnimationEditorPanel
          animation={state.animation}
          template={activeTemplate}
          dispatch={dispatch}
          onVariantChange={() => void loadPacks()}
        >
          <ScopeBanner
            pack={scoped.editedPack}
            busy={scoped.busy}
            onUseGlobal={scoped.revertToGlobal}
          />
        </AnimationEditorPanel>

        <main className="relative flex flex-1 flex-col overflow-hidden bg-neutral-950">
          <div
            className={
              'flex flex-1 overflow-hidden ' +
              (device === 'mobile' ? 'justify-center px-4 py-4' : '')
            }
          >
            {payload ? (
              <div
                className={
                  device === 'mobile'
                    ? 'h-full w-[375px] overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 shadow-2xl'
                    : 'h-full w-full'
                }
              >
                <AnimationPreview
                  ref={previewRef}
                  iframeOrigin={IFRAME_ORIGIN}
                  payload={payload}
                  onError={onPreviewError}
                />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center font-mono text-[12px] text-neutral-500">
                Select a pack with artwork to load the preview
              </div>
            )}
          </div>
          {previewError && (
            <div
              role="alert"
              className="absolute inset-x-4 bottom-4 rounded-md bg-black/80 px-3 py-2 font-mono text-[11px] text-red-300"
            >
              {previewError}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
